'use client'

import { useState } from 'react'
import { motion } from 'motion/react'
import { Shield, Award, Clock, MapPin } from 'lucide-react'
import { useMutation } from '@tanstack/react-query'
import { useServerFn } from '@tanstack/react-start'
import { updateProfileFn, uploadProfileAvatarFn } from '@/server/functions/auth'
import { useMk8Player } from '@/hooks/use-mk8-player'

const PLAYER = {
  name: 'SpeedRacer_NX',
  tag: '#MK8D-7721',
  mmr: 8420,
  tier: 'DIAMOND',
  region: 'EU-WEST',
  platform: 'Ryujinx',
  totalRaces: 1284,
  hoursPlayed: 312,
  favoriteChar: 'Waluigi',
  favoriteKart: 'Wild Wiggler',
  joined: 'JAN 2024',
}

const DRIVER_ALIASES: Record<string, string> = {
  LinkBotw: 'Link',
  Rosetta: 'Rosalina',
  Maskass: 'Shy Guy',
  YoshiBleu: 'Yoshi',
  YoshiRouge: 'Yoshi',
  YoshiVert: 'Yoshi',
  YoshiJaune: 'Yoshi',
  YoshiRose: 'Yoshi',
  YoshiNoir: 'Yoshi',
  YoshiBlanc: 'Yoshi',
  Harmonie: 'Rosalina',
  MarioMetal: 'Metal Mario',
  PeachOrRose: 'Pink Gold Peach',
}
const BODY_ALIASES: Record<string, string> = {
  B_Jet: 'Jet Bike',
  B_WildWiggler: 'Wild Wiggler',
}

function slugify(s = '') {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}
function normalizeDriver(v = '') {
  if (!v) return 'Unknown'
  const direct = DRIVER_ALIASES[v]
  if (direct) return direct
  const low = Object.keys(DRIVER_ALIASES).find((k) => k.toLowerCase() === v.toLowerCase())
  if (low) return DRIVER_ALIASES[low]
  return v
}
function normalizeBody(v = '') {
  return BODY_ALIASES[v] || v || 'Unknown'
}
function driverAsset(name: string) {
  return `/assets/drivers/${slugify(normalizeDriver(name))}.png`
}
function kartAsset(name: string) {
  const n = String(name || '').trim()
  // If we already have internal IDs like K_Std / B_Jet / V_Atv, use direct mapping
  if (/^[A-Z]_[A-Za-z0-9]+$/.test(n)) {
    return `/assets/karts/${encodeURIComponent(n)}.png`
  }
  // Fallback for human-readable labels
  return `/assets/karts/${slugify(normalizeBody(n))}.png`
}
function formatHMS(totalSec: number) {
  const sec = Math.max(0, Math.floor(Number(totalSec || 0)))
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

type Badge = { label: string; color: string }

function buildBadges(rank: number, races: number, avg: number, mmr: number): Badge[] {
  const badges: Badge[] = []

  if (rank >= 0 && rank < 100) badges.push({ label: 'Top 100', color: '#ffe600' })
  if (rank >= 0 && rank < 50) badges.push({ label: 'Top 50', color: '#ffd166' })
  if (rank >= 0 && rank < 10) badges.push({ label: 'Top 10', color: '#ff9f1c' })

  if (rank >= 0 && rank < 3) badges.push({ label: 'Win Streak ×7', color: '#00eaff' })
  else if (rank >= 0 && rank < 15) badges.push({ label: 'Win Streak ×5', color: '#3dd5f3' })
  else badges.push({ label: 'Win Streak ×3', color: '#7bdff2' })

  if (races >= 60) badges.push({ label: 'Track Master', color: '#ff3544' })
  if (avg >= 12) badges.push({ label: 'Bad Mood', color: '#a78bfa' })
  if (mmr >= 9000) badges.push({ label: 'Master League', color: '#00ff88' })
  else if (mmr >= 8000) badges.push({ label: 'Diamond League', color: '#00eaff' })

  return badges.slice(0, 6)
}

export function PlayerCard({ profileId }: { profileId?: string }) {
  const { currentUser, linkedName, myStats, rank, isOwnProfile, currentProfileId } = useMk8Player(profileId)
  const updateProfile = useServerFn(updateProfileFn)
  const uploadAvatar = useServerFn(uploadProfileAvatarFn)

  const [pseudo, setPseudo] = useState(currentUser?.name || '')
  const [emulatorUserId, setEmulatorUserId] = useState(String((currentUser as any)?.prefs?.emulatorUserId || ''))

  const saveMutation = useMutation({
    mutationFn: async () => {
      await updateProfile({ data: { pseudo, emulatorUserId } })
      window.location.reload()
    },
  })

  const avatarMutation = useMutation({
    mutationFn: async (dataUrl: string) => {
      await uploadAvatar({ data: { dataUrl } })
      window.location.reload()
    },
  })

  const onAvatarFile = async (f?: File | null) => {
    if (!f) return
    const reader = new FileReader()
    reader.onload = () => {
      const data = String(reader.result || '')
      if (data) avatarMutation.mutate(data)
    }
    reader.readAsDataURL(f)
  }

  const ownAvatarFileId = String((currentUser as any)?.prefs?.avatarFileId || '')
  const ownAvatarUpdatedAt = String((currentUser as any)?.prefs?.avatarUpdatedAt || '')
  const profileAvatarFileId = String((myStats as any)?.avatarFileId || '')
  const avatarFileId = isOwnProfile ? ownAvatarFileId : profileAvatarFileId
  const avatarVersion = isOwnProfile ? ownAvatarUpdatedAt : ''
  const avatarUrl = avatarFileId
    ? `/avatar?fileId=${encodeURIComponent(avatarFileId)}${avatarVersion ? `&v=${encodeURIComponent(avatarVersion)}` : ''}`
    : ''

  const player = {
    name: myStats?.name || linkedName || PLAYER.name,
    tag: '',
    mmr: myStats?.mmr || PLAYER.mmr,
    tier: myStats?.rankLabel || myStats?.rankName || 'Iron I',
    region: PLAYER.region,
    platform: PLAYER.platform,
    totalRaces: myStats?.races || PLAYER.totalRaces,
    raceTimeHms: myStats?.totalRaceTimeSec ? formatHMS(myStats.totalRaceTimeSec) : `${PLAYER.hoursPlayed}h`,
    favoriteChar: normalizeDriver(myStats?.bestDriver || (isOwnProfile ? PLAYER.favoriteChar : 'Unknown')),
    favoriteKart: normalizeBody(myStats?.bestBody || (isOwnProfile ? PLAYER.favoriteKart : 'Unknown')),
    joined: PLAYER.joined,
  }

  const badges = buildBadges(rank, player.totalRaces, myStats?.avg || 0, player.mmr)

  return (
    <section
      id="profile"
      className="relative py-24 px-6"
      style={{ background: '#08080e' }}
    >
      <div className="max-w-5xl mx-auto">
        <SectionLabel label="PLAYER PROFILE" />

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative rounded-2xl overflow-hidden p-8 md:p-10"
          style={{
            background:
              'linear-gradient(135deg, rgba(0,234,255,0.05) 0%, rgba(0,119,255,0.03) 100%)',
            border: '1px solid rgba(0,234,255,0.12)',
          }}
        >
          {/* Corner accent */}
          <div
            className="absolute top-0 right-0 w-48 h-48 opacity-10"
            style={{
              background:
                'radial-gradient(circle at 100% 0%, #00eaff, transparent 60%)',
            }}
          />

          <div className="flex flex-col md:flex-row gap-8 items-start">
            {/* Avatar */}
            <div className="relative flex-shrink-0">
              <div
                className="w-28 h-28 rounded-2xl flex items-center justify-center text-5xl relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #0d1a2e, #0d2b3e)',
                  border: '2px solid rgba(0,234,255,0.25)',
                }}
              >
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt="profile avatar"
                    onError={(e: any) => {
                      e.currentTarget.style.display = 'none'
                    }}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  '🏎️'
                )}
                {!avatarUrl ? (
                  <div
                    className="absolute inset-0 rounded-2xl blur-xl opacity-30"
                    style={{ background: '#00eaff' }}
                  />
                ) : null}
              </div>
              {isOwnProfile ? (
                <div className="mt-2">
                  <label
                    style={{
                      fontFamily: "'Barlow Condensed', sans-serif",
                      fontSize: '11px',
                      color: 'rgba(255,255,255,0.7)',
                      cursor: 'pointer',
                    }}
                  >
                    Change photo
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => onAvatarFile(e.target.files?.[0])} />
                  </label>
                </div>
              ) : null}
              {/* Online indicator */}
              <div
                className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-[#08080e]"
                style={{ background: '#00ff88' }}
              />
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h2
                  style={{
                    fontFamily: "'Russo One', sans-serif",
                    color: '#fff',
                    fontSize: '28px',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {player.name}
                </h2>
                {player.tag ? (
                  <span
                    style={{
                      fontFamily: "'Barlow Condensed', sans-serif",
                      color: 'rgba(255,255,255,0.35)',
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  >
                    {player.tag}
                  </span>
                ) : null}
              </div>

              {currentProfileId ? (
                <div className="mb-3">
                  <a
                    href={`/${currentProfileId}`}
                    style={{
                      fontFamily: "'Barlow Condensed', sans-serif",
                      fontSize: '12px',
                      color: '#00eaff',
                      letterSpacing: '0.04em',
                    }}
                  >
                    Profile link: /{currentProfileId}
                  </a>
                </div>
              ) : null}

              {/* Tier pill */}
              <div className="flex items-center gap-2 mb-4">
                <div
                  className="flex items-center gap-2 px-3 py-1 rounded-full"
                  style={{
                    background: 'rgba(0,234,255,0.1)',
                    border: '1px solid rgba(0,234,255,0.25)',
                  }}
                >
                  <Shield
                    size={12}
                    color="#00eaff"
                    fill="rgba(0,234,255,0.3)"
                  />
                  <span
                    style={{
                      fontFamily: "'Barlow Condensed', sans-serif",
                      fontWeight: 700,
                      letterSpacing: '0.15em',
                      color: '#00eaff',
                      fontSize: '11px',
                    }}
                  >
                    {player.tier} · {player.mmr.toLocaleString()} MMR
                  </span>
                </div>
              </div>

              {/* Badges */}
              <div className="flex flex-wrap gap-2 mb-6">
                {badges.map(({ label, color }) => (
                  <span
                    key={label}
                    className="flex items-center gap-1.5 px-3 py-1 rounded-md text-xs"
                    style={{
                      background: `${color}15`,
                      border: `1px solid ${color}30`,
                      color,
                      fontFamily: "'Barlow Condensed', sans-serif",
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                    }}
                  >
                    <Award size={11} />
                    {label}
                  </span>
                ))}
              </div>

              {isOwnProfile ? (
                <div
                  className="rounded-xl p-3 mb-4"
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.05)',
                  }}
                >
                  <p
                    style={{
                      fontFamily: "'Barlow Condensed', sans-serif",
                      fontSize: '11px',
                      letterSpacing: '0.16em',
                      color: 'rgba(255,255,255,0.45)',
                    }}
                    className="mb-2"
                  >
                    PROFILE SETTINGS
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <input
                      value={pseudo}
                      onChange={(e) => setPseudo(e.target.value)}
                      placeholder="Pseudo"
                      style={{
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(0,234,255,0.2)',
                        color: '#fff',
                        padding: '8px 10px',
                        borderRadius: 8,
                        fontSize: 13,
                      }}
                    />
                    <input
                      value={emulatorUserId}
                      onChange={(e) => setEmulatorUserId(e.target.value)}
                      placeholder="Emulator User ID"
                      style={{
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(0,234,255,0.2)',
                        color: '#fff',
                        padding: '8px 10px',
                        borderRadius: 8,
                        fontSize: 13,
                      }}
                    />
                  </div>
                  <button
                    onClick={() => saveMutation.mutate()}
                    disabled={saveMutation.isPending}
                    style={{
                      marginTop: 10,
                      background: 'linear-gradient(90deg, #00eaff, #0077ff)',
                      color: '#08080e',
                      padding: '7px 12px',
                      borderRadius: 8,
                      fontWeight: 700,
                      fontSize: 12,
                      letterSpacing: '0.08em',
                    }}
                  >
                    {saveMutation.isPending ? 'SAVING...' : 'SAVE PROFILE'}
                  </button>
                </div>
              ) : null}

              {/* Meta info grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { icon: MapPin, label: 'REGION', value: PLAYER.region },
                  {
                    icon: Clock,
                    label: 'RACE TIME',
                    value: player.raceTimeHms,
                  },
                  {
                    icon: Award,
                    label: 'RACES',
                    value: player.totalRaces.toLocaleString(),
                  },
                ].map(({ icon: Icon, label, value }) => (
                  <div
                    key={label}
                    className="rounded-xl p-3"
                    style={{
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.05)',
                    }}
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <Icon size={11} color="rgba(255,255,255,0.35)" />
                      <span
                        style={{
                          fontFamily: "'Barlow Condensed', sans-serif",
                          fontSize: '10px',
                          letterSpacing: '0.15em',
                          color: 'rgba(255,255,255,0.35)',
                          fontWeight: 600,
                        }}
                      >
                        {label}
                      </span>
                    </div>
                    <span
                      style={{
                        fontFamily: "'Barlow Condensed', sans-serif",
                        fontSize: '15px',
                        fontWeight: 700,
                        color: '#fff',
                        letterSpacing: '0.02em',
                      }}
                    >
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Fav loadout */}
            <div
              className="flex-shrink-0 rounded-2xl p-5 min-w-[160px]"
              style={{
                background: 'rgba(255,255,255,0.025)',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <p
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontSize: '10px',
                  letterSpacing: '0.2em',
                  color: 'rgba(255,255,255,0.3)',
                  fontWeight: 600,
                }}
                className="mb-3"
              >
                MAIN LOADOUT
              </p>
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <img
                    src={driverAsset(player.favoriteChar)}
                    alt={player.favoriteChar}
                    onError={(e: any) => {
                      e.currentTarget.src = '/assets/drivers/mii.png'
                    }}
                    style={{ width: 28, height: 28, borderRadius: 999 }}
                  />
                  <div>
                    <p
                      style={{
                        fontFamily: "'Barlow Condensed', sans-serif",
                        fontSize: '10px',
                        color: 'rgba(255,255,255,0.3)',
                        letterSpacing: '0.12em',
                      }}
                    >
                      CHARACTER
                    </p>
                    <p
                      style={{
                        fontFamily: "'Barlow Condensed', sans-serif",
                        fontWeight: 700,
                        fontSize: '14px',
                        color: '#fff',
                      }}
                    >
                      {player.favoriteChar}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <img
                    src={kartAsset(player.favoriteKart)}
                    alt={player.favoriteKart}
                    onError={(e: any) => {
                      e.currentTarget.src = '/assets/karts/fallback.svg'
                    }}
                    style={{ width: 34, height: 20, objectFit: 'contain' }}
                  />
                  <div>
                    <p
                      style={{
                        fontFamily: "'Barlow Condensed', sans-serif",
                        fontSize: '10px',
                        color: 'rgba(255,255,255,0.3)',
                        letterSpacing: '0.12em',
                      }}
                    >
                      KART
                    </p>
                    <p
                      style={{
                        fontFamily: "'Barlow Condensed', sans-serif",
                        fontWeight: 700,
                        fontSize: '14px',
                        color: '#fff',
                      }}
                    >
                      {player.favoriteKart}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export function SectionLabel({ label }: { label: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="flex items-center gap-3 mb-8"
    >
      <div className="w-1 h-5 rounded-full" style={{ background: '#00eaff' }} />
      <span
        style={{
          fontFamily: "'Barlow Condensed', sans-serif",
          fontWeight: 700,
          letterSpacing: '0.25em',
          color: 'rgba(255,255,255,0.4)',
          fontSize: '12px',
        }}
      >
        {label}
      </span>
    </motion.div>
  )
}
